import { Link, useLocation } from "react-router-dom";
import { OctonLogo } from "@/components/ui/OctonLogo";

// Seller details shown on every legal page. Edit here if they change.
export const SELLER = {
  trading: "OCTON Gym",
  legal: "David Finnegan",
  address: "Quinta do Peru, Lote 256, 2975-666 Setúbal, Portugal",
  email: "david.jesse.finnegan@gmail.com",
  phone: "+44 7900 223061",
  site: "octon-gym.com",
};
const UPDATED = "17 October 2026";

function H({ children }) {
  return <h2 className="font-heading font-bold text-xl tracking-tight mt-10 mb-3 text-white">{children}</h2>;
}
function P({ children }) {
  return <p className="text-[#B7BECB] leading-relaxed mb-3">{children}</p>;
}
function L({ items }) {
  return (
    <ul className="list-disc pl-6 space-y-1.5 mb-3 text-[#B7BECB]">
      {items.map((i) => <li key={i}>{i}</li>)}
    </ul>
  );
}

export function LegalFooterLinks({ className = "" }) {
  return (
    <nav className={`flex flex-wrap gap-x-5 gap-y-2 text-sm ${className}`}>
      <Link to="/terms" className="hover:text-white">Terms</Link>
      <Link to="/privacy" className="hover:text-white">Privacy</Link>
      <Link to="/refunds" className="hover:text-white">Cancellation &amp; refunds</Link>
      <Link to="/contact" className="hover:text-white">Contact</Link>
    </nav>
  );
}

function Shell({ title, children }) {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="px-6 md:px-12 py-5 border-b border-[#1E222A] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <OctonLogo size={30} glow={false} />
          <span className="font-heading font-black tracking-tight">OCTON Gym</span>
        </Link>
        <Link to="/login" className="text-sm text-[#8A91A6] hover:text-white">Sign in</Link>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12">
        <p className="mono-label text-acid">legal</p>
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight mt-2">{title}</h1>
        <p className="text-[#565C6F] text-sm mt-2">Last updated {UPDATED}</p>
        <div className="mt-8">{children}</div>
      </main>
      <footer className="px-6 md:px-12 py-8 border-t border-[#1E222A] flex flex-col md:flex-row gap-4 justify-between text-[#565C6F] text-sm">
        <span>{SELLER.trading} is a trading name of {SELLER.legal}, {SELLER.address}.</span>
        <LegalFooterLinks />
      </footer>
    </div>
  );
}

function Terms() {
  return (
    <Shell title="Terms of Service">
      <H>1. Who we are</H>
      <P>{SELLER.trading} ("we", "us") is a trading name of {SELLER.legal}, a sole trader established in Portugal at {SELLER.address}. You can reach us at {SELLER.email}. These terms apply when you create an account or buy a membership at {SELLER.site}.</P>

      <H>2. The service</H>
      <P>OCTON Gym is an online training service. It includes OCTON, an AI coach that suggests workouts, tracks your progress and gives feedback by text and voice, plus a workout library, class booking and, on some plans, access to a human coach. What each plan includes is shown on the membership page at the time you buy.</P>

      <H>3. Health and safety</H>
      <L items={[
        "OCTON Gym gives general fitness guidance. It is not medical advice, diagnosis or treatment, and OCTON is not a doctor or physiotherapist.",
        "Speak to a doctor before starting a new exercise programme, especially if you are pregnant, injured, have a heart condition or any other medical condition, or have not exercised for a while.",
        "Stop exercising and seek medical help if you feel pain, dizziness, chest discomfort or shortness of breath.",
        "You train at your own risk and are responsible for using suitable equipment and a safe space.",
        "AI suggestions can be wrong. Use your own judgement and do not follow any suggestion that feels unsafe.",
      ]} />

      <H>4. Your account</H>
      <P>You must be at least 18 years old to buy a membership. Keep your login details private; you are responsible for activity on your account. Tell us straight away if you think someone else has used it.</P>

      <H>5. Free taster</H>
      <P>New members get a free 7-day taster. No card is needed and nothing is charged when it ends; you simply choose a plan if you want to keep going.</P>

      <H>6. Prices and payment</H>
      <L items={[
        "Memberships are monthly subscriptions, charged in euros, pounds sterling or US dollars as you choose at checkout. Prices include any VAT that applies.",
        "Payments are processed by Stripe. We never see or store your full card details.",
        "Your membership renews automatically each month on the same date until you cancel. We charge the card or payment method you gave us.",
        "If a payment fails we may pause premium features until it is paid.",
        "We may change prices for future months. We will email you at least 30 days before a change affects you, and you can cancel before it takes effect.",
      ]} />

      <H>6a. Club plans</H>
      <L items={[
        "The Club plan lets a club, gym or team owner give up to 30 athletes access to OCTON Gym while the plan is active. The club owner pays; athletes join free with the club's code.",
        "When you join a club you agree that the club's owner and coaches can see your training activity: sessions, lifts, PRs, consistency and class attendance. They cannot see your coach chats, wellbeing check-ins or payment details.",
        "Club owners must only invite athletes who have agreed to join, and are responsible for how they use the information they see. Athletes under 18 may only join with a parent or guardian's consent.",
        "If the club's plan ends, athletes keep their account and data and can continue on an Individual plan.",
        "You can leave a club at any time, and a club owner can remove an athlete from the club.",
      ]} />

      <H>7. Cancelling</H>
      <P>You can cancel at any time from Membership, then Manage billing, or by emailing us. Your plan stays active until the end of the month you have paid for and is not renewed. See our <Link to="/refunds" className="text-acid underline">Cancellation and refund policy</Link>, which includes your 14-day right to withdraw as an EU or UK consumer.</P>

      <H>8. Acceptable use</H>
      <P>Do not misuse the service: no attempts to break or overload it, copy or resell it, access other members' data, or upload unlawful or abusive content. We may suspend or close accounts that do, and will explain why unless the law prevents us.</P>

      <H>9. Your content and data</H>
      <P>You keep ownership of what you upload (for example progress photos, notes and workout logs). You allow us to use it only to run the service for you. How we handle personal data is explained in our <Link to="/privacy" className="text-acid underline">Privacy Policy</Link>.</P>

      <H>10. Availability and changes</H>
      <P>We work to keep OCTON Gym available but cannot promise it will always be uninterrupted or error-free. We may improve or change features; if a change significantly reduces what your paid plan includes, you may cancel and receive a pro-rata refund for the unused part of the month.</P>

      <H>11. Our liability</H>
      <P>Nothing in these terms limits liability for death or personal injury caused by our negligence, for fraud, or any other liability that cannot be limited by law, and nothing affects your statutory rights as a consumer. Otherwise, we are not liable for losses that were not foreseeable, and our total liability to you is limited to the amount you paid us in the 12 months before the claim.</P>

      <H>12. Law and disputes</H>
      <P>These terms are governed by Portuguese law. If you are a consumer living elsewhere in the EU or in the UK, you also keep the protection of the mandatory laws of your country and may bring a claim in your local courts. Please contact us first so we can try to fix any problem. You may also use the EU's online dispute resolution information or a Portuguese consumer arbitration centre (see www.consumidor.gov.pt).</P>

      <H>13. Changes to these terms</H>
      <P>We may update these terms. If a change is significant we will tell you by email or in the app before it applies to you. The version you accepted at checkout is recorded with your account.</P>
    </Shell>
  );
}

function Privacy() {
  return (
    <Shell title="Privacy Policy">
      <P>This policy explains how {SELLER.trading} handles your personal data under the EU and UK General Data Protection Regulation (GDPR).</P>

      <H>Controller</H>
      <P>{SELLER.legal}, trading as {SELLER.trading}, {SELLER.address}. Contact: {SELLER.email}.</P>

      <H>What we collect</H>
      <L items={[
        "Account details: name, email address, password (stored only as a secure hash) or your Google sign-in.",
        "Training data you give us: workouts, sets, progress measurements, body metrics, nutrition logs, photos, notes and your conversations with OCTON (text and voice).",
        "Health-related data: some of your training and body data may count as health data. We process it only because you choose to give it to us for coaching, and with your explicit consent, which you can withdraw at any time by deleting the data or your account.",
        "Billing details: plan, currency, payment status and Stripe customer reference. Card details are handled by Stripe, not by us.",
        "Club membership: if you join a club, your club's owner and coaches see your training activity (sessions, lifts, PRs, consistency, class attendance) and any notes they write about your training. They do not see your coach chats, wellbeing check-ins or payments.",
        "Usage data: which parts of the app you open, used only in anonymous totals to improve OCTON Gym.",
        "Technical data: login session cookie, basic server logs (IP address, time, pages requested) for security.",
      ]} />

      <H>Why we use it (and legal basis)</H>
      <L items={[
        "To provide the service and your coaching (performance of our contract with you).",
        "To process health-related training data for coaching (your explicit consent).",
        "To take payments and keep accounting records (contract and legal obligation).",
        "To keep the service secure and fix problems (our legitimate interests).",
        "To send service emails such as receipts and important changes. We do not send marketing without your consent.",
      ]} />

      <H>Who we share it with</H>
      <P>We use trusted processors who act on our instructions:</P>
      <L items={[
        "Stripe (payments).",
        "Google (optional sign-in).",
        "AI providers that power the OCTON coach: OpenAI, Anthropic and Google (Gemini). Your messages and relevant training context are sent to them to generate replies. Under their business API terms they do not use this data to train their models.",
        "Cloudflare (secure connection to our servers).",
      ]} />
      <P>Some of these providers are in the United States. Transfers are covered by the EU-US Data Privacy Framework and/or Standard Contractual Clauses. We never sell your data.</P>

      <H>Where it is stored and for how long</H>
      <P>Your account and training data are stored on our own server, protected by encrypted connections. We keep it while your account is open. If you delete your account we erase your data within 30 days, except billing records, which we must keep for up to 10 years under Portuguese tax law.</P>

      <H>Your rights</H>
      <P>You can ask to access, correct, delete or export your data, object to or restrict how we use it, and withdraw consent at any time. Email {SELLER.email}; we will reply within one month. You can also complain to the Portuguese data protection authority, CNPD (www.cnpd.pt), or your local authority (in the UK, the ICO).</P>

      <H>Cookies</H>
      <P>We use one essential cookie to keep you signed in, and Stripe sets cookies on its checkout page to prevent fraud. We do not use advertising or tracking cookies.</P>

      <H>Children</H>
      <P>OCTON Gym is not intended for anyone under 18.</P>
    </Shell>
  );
}

function Refunds() {
  return (
    <Shell title="Cancellation & refund policy">
      <H>Cancel any time</H>
      <P>Go to Membership in the app and choose Manage billing, then Cancel plan. Or email {SELLER.email}. You keep access until the end of the month you have already paid for, and you will not be charged again.</P>

      <H>Your 14-day right to withdraw (EU and UK consumers)</H>
      <P>If you are a consumer, you can withdraw from a new membership within 14 days of buying it without giving a reason. To do so, email {SELLER.email} with your name, account email and the words "I withdraw from my OCTON Gym membership", or cancel in the app and tell us you are withdrawing.</P>
      <P>Because your membership starts straight away at your request, if you withdraw within the 14 days we refund what you paid minus a proportionate amount for the days you have already used. We refund within 14 days of receiving your request, to the same payment method.</P>

      <H>After the first 14 days</H>
      <P>Monthly payments already made are not refundable, except where the law requires it or where something has gone wrong on our side (for example a duplicate charge or a long outage). If you think you have been charged in error, contact us and we will put it right.</P>

      <H>Changing plan</H>
      <P>You can upgrade or downgrade from Manage billing. Upgrades apply straight away with the difference charged pro rata; downgrades apply from your next billing date.</P>
    </Shell>
  );
}

function Contact() {
  return (
    <Shell title="Contact us">
      <P>We are happy to help with your account, billing or anything else.</P>
      <div className="surface rounded-xl p-6 space-y-3 mt-6">
        <p><span className="mono-label block text-[#8A91A6]">email</span>
          <a href={`mailto:${SELLER.email}`} className="text-acid">{SELLER.email}</a></p>
        <p><span className="mono-label block text-[#8A91A6]">phone</span>{SELLER.phone}</p>
        <p><span className="mono-label block text-[#8A91A6]">address</span>{SELLER.legal}, trading as {SELLER.trading}<br />{SELLER.address}</p>
      </div>
      <P><span className="block mt-6">We aim to reply within two working days.</span></P>
    </Shell>
  );
}

export default function Legal() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/privacy")) return <Privacy />;
  if (pathname.startsWith("/refunds")) return <Refunds />;
  if (pathname.startsWith("/contact")) return <Contact />;
  return <Terms />;
}
